import React, { useState, useEffect, useMemo } from 'react';
import {
  Palmtree, Plus, Search, Loader2, X, Check,
  User, ChevronDown, ChevronUp, Edit2, Trash2,
  Calendar, AlertCircle, CheckCircle, Clock,
  TrendingDown, Filter
} from 'lucide-react';
import api from '../../services/api';
import { Toast } from '../ui/Toast';
import ConfirmDialog from '../ConfirmDialog';
import { useLoader } from '../../contexts/LoaderContext';
import { useParamsCompta } from '../../contexts/ParametresComptaContext';

const TYPES_CONGE = {
  annuel: { label: 'Congé annuel', color: '#16a34a', bg: '#f0fdf4' },
  sans_solde: { label: 'Sans solde', color: '#64748b', bg: '#f8fafc' },
  maladie: { label: 'Maladie', color: '#dc2626', bg: '#fef2f2' },
  maternite: { label: 'Maternité', color: '#7c3aed', bg: '#f5f3ff' },
  exceptionnel: { label: 'Exceptionnel', color: '#d97706', bg: '#fffbeb' },
};

const STATUTS = {
  planifie: { label: 'Planifié', color: '#2563eb', bg: '#eff6ff' },
  en_cours: { label: 'En cours', color: '#16a34a', bg: '#f0fdf4' },
  termine: { label: 'Terminé', color: '#64748b', bg: '#f8fafc' },
  annule: { label: 'Annulé', color: '#dc2626', bg: '#fef2f2' },
};

const MOIS_NOMS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
};

const CongesModule = () => {
  const anneeActuelle = new Date().getFullYear();

  const [conges, setConges] = useState([]);
  const [employes, setEmployes] = useState([]);
  const [soldes, setSoldes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('tous');
  const [filterStatut, setFilterStatut] = useState('tous');
  const [filterAnnee, setFilterAnnee] = useState(anneeActuelle);
  const [activeView, setActiveView] = useState('liste'); // 'liste' | 'calendrier' | 'soldes'
  const [showForm, setShowForm] = useState(false);
  const [editingConge, setEditingConge] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [expandedEmployee, setExpandedEmployee] = useState(null);

  const { showLoader, hideLoader } = useLoader();

  const [form, setForm] = useState({
    employe_id: '',
    type_conge: 'annuel',
    date_debut: '',
    date_fin: '',
    est_paye: 1,
    motif: ''
  });

  const showToast = (message, type = 'success') => setToast({ show: true, message, type });

  useEffect(() => {
    loadData();
  }, [filterAnnee]);

  const loadData = async () => {
    showLoader();
    setLoading(true);
    try {
      const [congesRes, employesRes, soldesRes] = await Promise.all([
        api.conges.getAll({ annee: filterAnnee }),
        api.comptabilite.getPointagesMensuelsEmployes(
          new Date().getMonth() + 1, filterAnnee
        ),
        api.conges.getSoldes(filterAnnee)
      ]);
      if (congesRes.success) setConges(congesRes.data);
      if (employesRes.success) setEmployes(employesRes.data);
      if (soldesRes.success) setSoldes(soldesRes.data);
    } catch (e) {
      showToast('Erreur lors du chargement', 'error');
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  // ============================================================================
  // FORM HELPERS
  // ============================================================================
  const getNbJours = () => {
    if (!form.date_debut || !form.date_fin) return 0;
    const d1 = new Date(form.date_debut);
    const d2 = new Date(form.date_fin);
    return Math.max(1, Math.ceil((d2 - d1) / (1000 * 60 * 60 * 24)) + 1);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingConge(null);
    setForm({ employe_id: '', type_conge: 'annuel', date_debut: '', date_fin: '', est_paye: 1, motif: '' });
  };

  // ============================================================================
  // CRUD
  // ============================================================================
  const handleSubmit = async () => {
    if (!form.employe_id || !form.date_debut || !form.date_fin) {
      showToast('Employé, date début et date fin requis', 'error');
      return;
    }
    setLoading(true);
    try {
      if (editingConge) {
        const res = await api.conges.modifier(editingConge.id, form);
        if (res.success) { showToast('Congé modifié'); resetForm(); loadData(); }
        else showToast(res.message || 'Erreur', 'error');
      } else {
        const res = await api.conges.creer(form);
        if (res.success) {
          showToast(`Congé créé — ${res.data.nb_jours} jour(s)`);
          resetForm(); loadData();
        } else showToast(res.message || 'Erreur', 'error');
      }
    } catch (e) {
      showToast(e.message || 'Erreur', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (conge) => {
    setEditingConge(conge);
    setForm({
      employe_id: conge.employe_id,
      type_conge: conge.type_conge,
      date_debut: conge.date_debut?.split('T')[0] || conge.date_debut,
      date_fin: conge.date_fin?.split('T')[0] || conge.date_fin,
      est_paye: conge.est_paye,
      motif: conge.motif || ''
    });
    setShowForm(true);
  };

  // ============================================================================
  // DONNÉES FILTRÉES
  // ============================================================================
  const congesFiltres = useMemo(() => {
    return conges.filter(c => {
      const nomComplet = `${c.employe_prenom} ${c.employe_nom}`.toLowerCase();
      const matchSearch = nomComplet.includes(searchTerm.toLowerCase());
      const matchType = filterType === 'tous' || c.type_conge === filterType;
      const matchStatut = filterStatut === 'tous' || c.statut === filterStatut;
      return matchSearch && matchType && matchStatut;
    });
  }, [conges, searchTerm, filterType, filterStatut]);

  // Grouper par employé
  const groupesParEmploye = useMemo(() => {
    const groups = {};
    congesFiltres.forEach(c => {
      if (!groups[c.employe_id]) {
        groups[c.employe_id] = {
          employe_id: c.employe_id,
          nom: `${c.employe_prenom} ${c.employe_nom}`,
          departement: c.departement,
          conges: []
        };
      }
      groups[c.employe_id].conges.push(c);
    });
    return Object.values(groups).sort((a, b) => a.nom.localeCompare(b.nom));
  }, [congesFiltres]);

  // Stats
  const stats = useMemo(() => ({
    total: conges.length,
    en_cours: conges.filter(c => c.statut === 'en_cours').length,
    planifie: conges.filter(c => c.statut === 'planifie').length,
    total_jours: conges.reduce((sum, c) => sum + parseFloat(c.nb_jours || 0), 0),
  }), [conges]);

  const inputCls = 'w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium focus:outline-none transition-all';

  // ============================================================================
  // VUE CALENDRIER
  // ============================================================================
  const [calMois, setCalMois] = useState(new Date().getMonth());
  const [calAnnee, setCalAnnee] = useState(new Date().getFullYear());

  const renderCalendrier = () => {
    const premierJour = new Date(calAnnee, calMois, 1);
    const dernierJour = new Date(calAnnee, calMois + 1, 0);
    const joursTotal = dernierJour.getDate();
    const debutSemaine = premierJour.getDay() === 0 ? 6 : premierJour.getDay() - 1;

    const congesDuMois = conges.filter(c => {
      const debut = new Date(c.date_debut);
      const fin = new Date(c.date_fin);
      const debutMois = new Date(calAnnee, calMois, 1);
      const finMois = new Date(calAnnee, calMois + 1, 0);
      return debut <= finMois && fin >= debutMois;
    });

    const getCongesJour = (jour) => {
      const date = new Date(calAnnee, calMois, jour);
      return congesDuMois.filter(c => {
        const debut = new Date(c.date_debut);
        const fin = new Date(c.date_fin);
        return date >= debut && date <= fin;
      });
    };

    const jours = [];
    for (let i = 0; i < debutSemaine; i++) jours.push(null);
    for (let i = 1; i <= joursTotal; i++) jours.push(i);

    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        {/* Navigation mois */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => {
            if (calMois === 0) { setCalMois(11); setCalAnnee(a => a - 1); }
            else setCalMois(m => m - 1);
          }} className="p-2 rounded-lg hover:bg-gray-100 transition-all">
            <ChevronDown size={18} className="rotate-90" />
          </button>
          <h3 className="text-lg font-bold text-gray-900">
            {MOIS_NOMS[calMois]} {calAnnee}
          </h3>
          <button onClick={() => {
            if (calMois === 11) { setCalMois(0); setCalAnnee(a => a + 1); }
            else setCalMois(m => m + 1);
          }} className="p-2 rounded-lg hover:bg-gray-100 transition-all">
            <ChevronDown size={18} className="-rotate-90" />
          </button>
        </div>

        {/* En-têtes jours */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(j => (
            <div key={j} className="text-center text-xs font-bold text-gray-500 py-2">{j}</div>
          ))}
        </div>

        {/* Jours */}
        <div className="grid grid-cols-7 gap-1">
          {jours.map((jour, i) => {
            if (!jour) return <div key={`empty-${i}`} />;
            const congesJour = getCongesJour(jour);
            const isToday = new Date().getDate() === jour &&
              new Date().getMonth() === calMois && new Date().getFullYear() === calAnnee;

            return (
              <div key={jour} className={`min-h-16 p-1 rounded-lg border transition-all ${isToday ? 'border-2 border-teal-400' : 'border-gray-100'
                } ${congesJour.length > 0 ? 'bg-green-50/30' : 'bg-white hover:bg-gray-50'}`}>
                <div className={`text-xs font-bold mb-1 w-6 h-6 flex items-center justify-center rounded-full ${isToday ? 'bg-teal-500 text-white' : 'text-gray-700'
                  }`}>{jour}</div>
                <div className="space-y-0.5">
                  {congesJour.slice(0, 2).map(c => {
                    const typeInfo = TYPES_CONGE[c.type_conge] || TYPES_CONGE.annuel;
                    return (
                      <div key={c.id} className="text-[10px] font-semibold px-1 py-0.5 rounded truncate"
                        style={{ backgroundColor: typeInfo.bg, color: typeInfo.color }}>
                        {c.employe_prenom} {c.employe_nom?.charAt(0)}.
                      </div>
                    );
                  })}
                  {congesJour.length > 2 && (
                    <div className="text-[10px] text-gray-400 font-medium">+{congesJour.length - 2}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Légende */}
        <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-gray-100">
          {Object.entries(TYPES_CONGE).map(([key, val]) => (
            <div key={key} className="flex items-center gap-1.5 text-xs font-medium">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: val.color }} />
              <span className="text-gray-600">{val.label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ============================================================================
  // VUE SOLDES
  // ============================================================================
  const renderSoldes = () => (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      {soldes.length === 0 ? (
        <div className="p-12 text-center text-gray-400">
          <TrendingDown size={48} className="mx-auto mb-4 opacity-20" />
          <p className="font-semibold">Aucun solde pour {filterAnnee}</p>
          <p className="text-sm mt-1">Les soldes sont créés automatiquement lors du premier congé</p>
        </div>
      ) : (
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">Employé</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Jours acquis</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Jours pris</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Jours restants</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Progression</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {soldes.map(s => {
              const pct = s.jours_acquis > 0
                ? Math.round((s.jours_pris / s.jours_acquis) * 100)
                : 0;
              const couleur = pct > 80 ? '#dc2626' : pct > 50 ? '#d97706' : '#16a34a';
              return (
                <tr key={s.id} className="hover:bg-gray-50/50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: '#448696' }}>
                        <User size={16} className="text-white" />
                      </div>
                      <div>
                        <div className="text-sm font-bold text-gray-900">
                          {s.employe_prenom} {s.employe_nom}
                        </div>
                        <div className="text-xs text-gray-500">{s.departement || 'N/A'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className="text-sm font-bold text-gray-900">{s.jours_acquis}j</span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className="text-sm font-bold text-orange-600">{s.jours_pris}j</span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className="text-sm font-bold" style={{ color: couleur }}>
                      {s.jours_restants}j
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div className="h-2 rounded-full transition-all"
                          style={{ width: `${Math.min(100, pct)}%`, backgroundColor: couleur }} />
                      </div>
                      <span className="text-xs font-bold w-8 text-right" style={{ color: couleur }}>
                        {pct}%
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );

  // ============================================================================
  // RENDU PRINCIPAL
  // ============================================================================
  return (
    <div className="space-y-6">

      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">


        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center gap-2 px-5 py-3 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all text-sm"
          style={{ backgroundColor: '#0ea5e9' }}>
          <Plus size={18} /> Nouveau congé
        </button>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Total congés', value: stats.total, color: '#0ea5e9', bg: '#f0f9ff' },
          { label: 'En cours', value: stats.en_cours, color: '#16a34a', bg: '#f0fdf4' },
          { label: 'Planifiés', value: stats.planifie, color: '#2563eb', bg: '#eff6ff' },
          { label: 'Jours total', value: `${stats.total_jours}j`, color: '#d97706', bg: '#fffbeb' },
        ].map((k, i) => (
          <div key={i} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100"
            style={{ borderLeft: `3px solid ${k.color}` }}>
            <div className="text-xs font-semibold text-gray-500 mb-2">{k.label}</div>
            <div className="text-2xl font-bold" style={{ color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* ONGLETS VUES */}
      <div className="flex items-center gap-2">
        {[
          { id: 'liste', label: 'Liste', icon: Filter },
          { id: 'calendrier', label: 'Calendrier', icon: Calendar },
          { id: 'soldes', label: 'Soldes congés', icon: TrendingDown },
        ].map(v => {
          const Icon = v.icon;
          return (
            <button key={v.id} onClick={() => setActiveView(v.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${activeView === v.id ? 'text-white shadow-sm' : 'text-gray-600 bg-white border border-gray-200 hover:bg-gray-50'
                }`}
              style={{ backgroundColor: activeView === v.id ? '#0ea5e9' : undefined }}>
              <Icon size={15} /> {v.label}
            </button>
          );
        })}

        {/* Filtre année */}
        <div className="ml-auto flex items-center gap-2">
          <select value={filterAnnee} onChange={e => setFilterAnnee(parseInt(e.target.value))}
            className="px-3 py-2 border border-gray-200 rounded-xl text-sm font-medium bg-white focus:outline-none">
            {[2024, 2025, 2026, 2027].map(a => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
      </div>

      {/* FORMULAIRE */}
      {showForm && (
        <div className="bg-white rounded-2xl shadow-sm border-2 p-6"
          style={{ borderColor: '#0ea5e9' }}>
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-bold text-gray-900">
              {editingConge ? 'Modifier le congé' : 'Nouveau congé'}
            </h3>
            <button onClick={resetForm} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400">
              <X size={18} />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Employé */}
            {!editingConge && (
              <div className="col-span-2">
                <label className="text-xs font-semibold text-gray-600 block mb-1.5">Employé *</label>
                <select value={form.employe_id}
                  onChange={e => setForm(f => ({ ...f, employe_id: e.target.value }))}
                  className={inputCls}>
                  <option value="">-- Sélectionner un employé --</option>
                  {employes.map(e => (
                    <option key={e.id} value={e.id}>
                      {e.prenom} {e.nom} {e.departement ? `— ${e.departement}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Type */}
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1.5">Type de congé *</label>
              <select value={form.type_conge}
                onChange={e => setForm(f => ({ ...f, type_conge: e.target.value }))}
                className={inputCls}>
                {Object.entries(TYPES_CONGE).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </select>
            </div>

            {/* Payé */}
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1.5">Rémunération</label>
              <select value={form.est_paye}
                onChange={e => setForm(f => ({ ...f, est_paye: parseInt(e.target.value) }))}
                className={inputCls}>
                <option value={1}>Congé payé</option>
                <option value={0}>Congé non payé</option>
              </select>
            </div>

            {/* Dates */}
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1.5">Date début *</label>
              <input type="date" value={form.date_debut}
                onChange={e => setForm(f => ({ ...f, date_debut: e.target.value }))}
                className={inputCls}
                onFocus={e => e.target.style.borderColor = '#0ea5e9'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1.5">Date fin *</label>
              <input type="date" value={form.date_fin}
                min={form.date_debut}
                onChange={e => setForm(f => ({ ...f, date_fin: e.target.value }))}
                className={inputCls}
                onFocus={e => e.target.style.borderColor = '#0ea5e9'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
            </div>

            {/* Nb jours calculé */}
            {form.date_debut && form.date_fin && (
              <div className="col-span-2">
                <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold"
                  style={{ backgroundColor: '#f0f9ff', color: '#0ea5e9', border: '1px solid #bae6fd' }}>
                  <Calendar size={16} />
                  Durée calculée : <strong>{getNbJours()} jour(s)</strong>
                </div>
              </div>
            )}

            {/* Motif */}
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-600 block mb-1.5">Motif (optionnel)</label>
              <textarea rows={2} value={form.motif}
                onChange={e => setForm(f => ({ ...f, motif: e.target.value }))}
                placeholder="Ex: Congés d'été, mariage..."
                className={`${inputCls} resize-none`}
                onFocus={e => e.target.style.borderColor = '#0ea5e9'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
            </div>
          </div>

          <div className="flex gap-3 justify-end mt-5">
            <button onClick={resetForm}
              className="px-5 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50">
              Annuler
            </button>
            <button onClick={handleSubmit} disabled={loading}
              className="px-6 py-2.5 text-white rounded-xl text-sm font-bold disabled:opacity-50 flex items-center gap-2"
              style={{ backgroundColor: '#0ea5e9' }}>
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              {editingConge ? 'Modifier' : 'Créer le congé'}
            </button>
          </div>
        </div>
      )}

      {/* FILTRES — vue liste uniquement */}
      {activeView === 'liste' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1" style={{ minWidth: '200px' }}>
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" placeholder="Rechercher un employé..."
                value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                className={`${inputCls} pl-9`} />
            </div>
            <select value={filterType} onChange={e => setFilterType(e.target.value)}
              className={inputCls} style={{ maxWidth: '180px' }}>
              <option value="tous">Tous les types</option>
              {Object.entries(TYPES_CONGE).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)}
              className={inputCls} style={{ maxWidth: '160px' }}>
              <option value="tous">Tous les statuts</option>
              {Object.entries(STATUTS).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* CONTENU PAR VUE */}
      {activeView === 'calendrier' && renderCalendrier()}
      {activeView === 'soldes' && renderSoldes()}

      {activeView === 'liste' && (
        loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={32} className="animate-spin" style={{ color: '#0ea5e9' }} />
          </div>
        ) : groupesParEmploye.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
            <Calendar size={48} className="mx-auto mb-4 text-gray-200" />
            <p className="text-lg font-semibold text-gray-400">Aucun congé trouvé</p>
            <p className="text-sm text-gray-400 mt-1">Cliquez sur "Nouveau congé" pour commencer</p>
          </div>
        ) : (
          <div className="space-y-3">
            {groupesParEmploye.map(groupe => (
              <div key={groupe.employe_id}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Header employé */}
                <button
                  onClick={() => setExpandedEmployee(
                    expandedEmployee === groupe.employe_id ? null : groupe.employe_id
                  )}
                  className="w-full flex items-center gap-4 px-6 py-4 hover:bg-gray-50/50 transition-all text-left">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: '#0ea5e9' }}>
                    <User size={18} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-bold text-gray-900">{groupe.nom}</div>
                    <div className="text-xs text-gray-500">{groupe.departement || 'N/A'}</div>
                  </div>
                  <div className="text-right mr-4">
                    <div className="text-sm font-bold" style={{ color: '#0ea5e9' }}>
                      {groupe.conges.reduce((sum, c) => sum + parseFloat(c.nb_jours), 0)}j
                    </div>
                    <div className="text-xs text-gray-400">
                      {groupe.conges.length} congé{groupe.conges.length > 1 ? 's' : ''}
                    </div>
                  </div>
                  {expandedEmployee === groupe.employe_id
                    ? <ChevronUp size={18} className="text-gray-400" />
                    : <ChevronDown size={18} className="text-gray-400" />}
                </button>

                {/* Détail congés */}
                {expandedEmployee === groupe.employe_id && (
                  <div className="border-t border-gray-100">
                    <table className="w-full">
                      <thead className="bg-gray-50/80">
                        <tr>
                          <th className="px-6 py-2.5 text-left text-xs font-semibold text-gray-600">Type</th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600">Période</th>
                          <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-600">Jours</th>
                          <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-600">Rémunération</th>
                          <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-600">Statut</th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600">Motif</th>
                          <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {groupe.conges.map(conge => {
                          const typeInfo = TYPES_CONGE[conge.type_conge] || TYPES_CONGE.annuel;
                          const statutInfo = STATUTS[conge.statut] || STATUTS.planifie;
                          return (
                            <tr key={conge.id} className="hover:bg-gray-50/50">
                              <td className="px-6 py-3">
                                <span className="px-2.5 py-1 rounded-lg text-xs font-semibold"
                                  style={{ backgroundColor: typeInfo.bg, color: typeInfo.color }}>
                                  {typeInfo.label}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-xs text-gray-700 font-medium">
                                  {formatDate(conge.date_debut)} → {formatDate(conge.date_fin)}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className="text-sm font-bold" style={{ color: '#0ea5e9' }}>
                                  {conge.nb_jours}j
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={`text-xs font-semibold px-2 py-1 rounded-lg ${conge.est_paye ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                  }`}>
                                  {conge.est_paye ? 'Payé' : 'Non payé'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className="px-2 py-1 rounded-lg text-xs font-semibold"
                                  style={{ backgroundColor: statutInfo.bg, color: statutInfo.color }}>
                                  {statutInfo.label}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-xs text-gray-500 line-clamp-1">
                                  {conge.motif || '—'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                {conge.statut !== 'termine' && (
                                  <div className="flex items-center justify-center gap-1">
                                    <button onClick={() => handleEdit(conge)}
                                      className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-all">
                                      <Edit2 size={14} />
                                    </button>
                                    <button onClick={() => setConfirmDialog(conge)}
                                      className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all">
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}

      {/* CONFIRM DIALOG annulation */}
      {confirmDialog && (
        <ConfirmDialog
          isOpen={true}
          title="Annuler ce congé"
          message={`Confirmer l'annulation du congé de ${confirmDialog.employe_prenom} ${confirmDialog.employe_nom} (${confirmDialog.nb_jours}j) ?`}
          type="warning"
          confirmText="Annuler le congé"
          cancelText="Retour"
          requireJustification={true}
          onConfirm={async (justification) => {
            try {
              const res = await api.conges.annuler(confirmDialog.id, justification);
              if (res.success) {
                showToast('Congé annulé avec succès');
                setConfirmDialog(null);
                loadData();
                return true;
              } else {
                showToast(res.message || 'Erreur', 'error');
                return false;
              }
            } catch (e) {
              showToast(e.message || 'Erreur', 'error');
              return false;
            }
          }}
          onCancel={() => setConfirmDialog(null)}
        />
      )}

      {/* TOAST */}
      {toast.show && (
        <Toast message={toast.message} type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: '' })} />
      )}
    </div>
  );
};

export default CongesModule;